import { products, Product, SkinType, AcneType } from '../data/products';
import { ScanRecord, AcneSeverity } from '../services/scanService';

/**
 * Recommends skincare products based on scan analysis
 * Prioritizes Korean beauty products and matches to user's skin condition
 * Filters by gender if specified (male = men's products, female = women's products, prefer_not_to_say = all products)
 */
export function getRecommendedProductsFromScan(scan: ScanRecord | null, limit: number = 6, userGender?: 'male' | 'female' | 'prefer_not_to_say'): Product[] {
  if (!scan?.analysis) {
    return [];
  }

  const analysis = scan.analysis;
  
  // Determine skin type from analysis
  let detectedSkinType: SkinType | undefined;
  const estimatedPH = analysis.acneAnalysis?.estimatedPH ?? analysis.estimatedPH;
  const textureNotes = (analysis.acneAnalysis?.textureSummary || analysis.textureNotes || '').toLowerCase();
  
  if (typeof estimatedPH === 'number') {
    if (estimatedPH < 4.7 || textureNotes.includes('sensitive')) {
      detectedSkinType = 'sensitive';
    } else if (estimatedPH > 5.9 || textureNotes.includes('dry')) {
      detectedSkinType = 'dry';
    } else if (textureNotes.includes('oily') || textureNotes.includes('t-zone')) {
      detectedSkinType = 'combination';
    } else if (estimatedPH >= 4.7 && estimatedPH <= 5.9) {
      detectedSkinType = 'normal';
    }
  } else {
    if (textureNotes.includes('sensitive')) detectedSkinType = 'sensitive';
    else if (textureNotes.includes('dry')) detectedSkinType = 'dry';
    else if (textureNotes.includes('oily') || textureNotes.includes('t-zone')) detectedSkinType = 'combination';
  }

  // Determine acne type from severity
  let detectedAcneType: AcneType | undefined;
  const acneSeverity = analysis.acneAnalysis?.overallSeverity || analysis.acneSeverity;
  
  if (acneSeverity) {
    if (acneSeverity === 'mild' || acneSeverity === 'very_mild' || acneSeverity === 'none') {
      detectedAcneType = 'mild';
    } else if (acneSeverity === 'moderate' || acneSeverity === 'mild_to_moderate') {
      detectedAcneType = 'moderate';
    } else if (acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
      detectedAcneType = 'severe';
    }
  }

  // Check for inflammatory acne
  const isInflammatory = analysis.acneAnalysis?.acneType?.inflammatory === 'moderate' || 
                         analysis.acneAnalysis?.acneType?.inflammatory === 'pronounced';

  // Determine concerns from comprehensive scan data
  const concerns: string[] = [];
  const regions = analysis.acneAnalysis?.regions || [];
  const glowScore = analysis.skinHealthIndex || 70;
  
  // Use estimatedPH for barrier health
  if (analysis.acneAnalysis?.estimatedPH && analysis.acneAnalysis.estimatedPH < 4.7) {
    concerns.push('damaged barrier');
  }
  
  // Use inflammatory acne detection
  if (isInflammatory) {
    concerns.push('inflammation', 'redness');
  }
  
  // Use comedonal acne detection
  if (analysis.acneAnalysis?.acneType?.comedonal === 'moderate' || 
      analysis.acneAnalysis?.acneType?.comedonal === 'pronounced') {
    concerns.push('clogged pores', 'texture');
  }
  
  // Use region-specific hyperpigmentation data
  const hyperpigmentationRegions = regions.filter(r => r.hyperpigmentationLevel !== 'none');
  if (hyperpigmentationRegions.length > 0) {
    const maxHyperpigmentation = hyperpigmentationRegions.reduce((max, r) => {
      const levels = { 'none': 0, 'mild': 1, 'moderate': 2, 'marked': 3 };
      return levels[r.hyperpigmentationLevel] > levels[max.hyperpigmentationLevel] ? r : max;
    }, hyperpigmentationRegions[0]);
    if (maxHyperpigmentation.hyperpigmentationLevel === 'marked' || maxHyperpigmentation.hyperpigmentationLevel === 'moderate') {
      concerns.push('dark spots', 'hyperpigmentation');
    } else {
      concerns.push('dark spots');
    }
  }
  
  // Use region-specific texture descriptions
  const textureDescriptions = regions.map(r => r.textureDescription.toLowerCase()).join(' ');
  if (textureDescriptions.includes('rough') || textureDescriptions.includes('uneven')) {
    concerns.push('texture', 'roughness');
  }
  if (textureDescriptions.includes('dry') || textureDescriptions.includes('dehydrated')) {
    concerns.push('dryness');
  }
  if (textureDescriptions.includes('oily') || textureDescriptions.includes('shiny')) {
    concerns.push('oiliness');
  }
  
  // Use glowScore for overall skin health concerns
  if (glowScore < 60) {
    concerns.push('needs attention');
  }
  
  // Use region-specific severity data
  const severeRegions = regions.filter(r => 
    r.severity === 'moderate' || r.severity === 'moderate_to_severe' || r.severity === 'severe'
  );
  if (severeRegions.length > 2) {
    concerns.push('widespread acne');
  }
  
  // Use total lesion counts for prioritization
  const totalActiveLesions = regions.reduce((sum, r) => sum + r.activeLesionCount, 0);
  const totalComedones = regions.reduce((sum, r) => sum + r.comedoneCount, 0);
  if (totalActiveLesions > 15) {
    concerns.push('active breakouts');
  }
  if (totalComedones > 10) {
    concerns.push('clogged pores', 'blackheads');
  }

  // Create a unique seed based on ALL scan data for consistent but personalized results
  const scanSeed = Math.floor(
    (glowScore * 100) +
    ((estimatedPH || 5.0) * 50) +
    (totalActiveLesions * 10) +
    (totalComedones * 5) +
    (hyperpigmentationRegions.length * 20) +
    (regions.length * 15)
  ) % 10000;
  
  // Filter products by gender if specified
  let filteredProducts = products;
  if (userGender === 'male') {
    // Filter for men's products - look for "men", "male", "grooming", "shaving" in product name/category
    filteredProducts = products.filter(p => {
      const productText = (p.brand + ' ' + p.productCategory).toLowerCase();
      return productText.includes('men') || 
             productText.includes('male') || 
             productText.includes('grooming') ||
             productText.includes('shaving') ||
             productText.includes('beard') ||
             // If no gender-specific keywords, include it (many products are unisex)
             (!productText.includes('women') && !productText.includes('female') && !productText.includes('ladies'));
    });
  } else if (userGender === 'female') {
    // Filter for women's products - exclude men-specific products
    filteredProducts = products.filter(p => {
      const productText = (p.brand + ' ' + p.productCategory).toLowerCase();
      return !productText.includes('men') && 
             !productText.includes('male') && 
             !productText.includes('grooming') &&
             !productText.includes('shaving') &&
             !productText.includes('beard');
    });
  }
  // If userGender is 'prefer_not_to_say' or undefined, show all products

  // Score products based on match with comprehensive scan data
  const scoredProducts = filteredProducts.map((product, index) => {
    let score = 0;

    // Skin type match (high priority)
    if (detectedSkinType && product.recommendedFor.skinTypes.includes(detectedSkinType)) {
      score += 10;
    }

    // Acne type match (high priority)
    if (detectedAcneType && product.recommendedFor.acneTypes.includes(detectedAcneType)) {
      score += 10;
    }

    // Concerns match (weighted by importance)
    concerns.forEach(concern => {
      if (product.recommendedFor.concerns.includes(concern)) {
        // Prioritize critical concerns
        if (concern === 'damaged barrier' || concern === 'active breakouts' || concern === 'widespread acne') {
          score += 8;
        } else if (concern === 'hyperpigmentation' || concern === 'dark spots') {
          score += 6;
        } else {
          score += 5;
        }
      }
    });
    
    // Use scan seed to add slight variation for different users with similar conditions
    // This ensures different users get different product recommendations even with similar skin
    const seedVariation = ((scanSeed + index * 17) % 100) / 100;
    score += seedVariation * 2; // Add up to 2 points of variation

    // Prioritize Korean beauty products
    if (product.tags.includes('k-beauty')) {
      score += 3;
    }

    // Prioritize budget-friendly options
    if (product.priceRange === '$') {
      score += 2;
    }

    // Prioritize gentle products for sensitive skin
    if (detectedSkinType === 'sensitive' && product.tags.includes('gentle')) {
      score += 5;
    }

    // Prioritize barrier repair for damaged barrier
    if (concerns.includes('damaged barrier') && product.tags.includes('barrier-repair')) {
      score += 5;
    }

    // Prioritize anti-inflammatory for inflammatory acne
    if (isInflammatory && product.tags.includes('anti-inflammatory')) {
      score += 5;
    }

    // Prioritize acne-control for moderate/severe acne
    if ((detectedAcneType === 'moderate' || detectedAcneType === 'severe') && 
        product.tags.includes('acne-control')) {
      score += 5;
    }

    // IMPORTANT: Prioritize moisturizers - always include at least one
    const isMoisturizer = product.productCategory.toLowerCase().includes('moisturizer') ||
                         product.productCategory.toLowerCase().includes('cream') ||
                         product.productCategory.toLowerCase().includes('gel') && 
                         product.productCategory.toLowerCase().includes('moisturizer');
    if (isMoisturizer) {
      score += 8; // High priority for moisturizers
    }

    return { product, score };
  });

  // Sort by score (highest first)
  const sortedProducts = scoredProducts
    .sort((a, b) => b.score - a.score)
    .map(item => item.product);

  // Ensure at least one moisturizer is included
  const moisturizers = sortedProducts.filter(p => 
    p.productCategory.toLowerCase().includes('moisturizer') ||
    (p.productCategory.toLowerCase().includes('cream') && 
     !p.productCategory.toLowerCase().includes('serum') &&
     !p.productCategory.toLowerCase().includes('treatment'))
  );

  // If we have moisturizers, ensure at least one is in the top results
  if (moisturizers.length > 0) {
    const topProducts = sortedProducts.slice(0, limit);
    const hasMoisturizer = topProducts.some(p => moisturizers.includes(p));
    
    if (!hasMoisturizer) {
      // Replace the last product with a moisturizer if none exists
      const result = [...topProducts.slice(0, limit - 1), moisturizers[0]];
      return result;
    }
  }

  return sortedProducts.slice(0, limit);
}

